import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cx } from '../cx';
import { Portal } from '../Portal';
import { useDismiss } from '../hooks/useDismiss';
import { usePosition, type Placement } from '../hooks/usePosition';

export type TooltipProps = {
  /** Short supplementary text. Never the only place essential information lives. */
  content: ReactNode;
  /** One focusable element (a button, a link); it gets aria-describedby while the tooltip shows. */
  children: ReactElement<Record<string, unknown>>;
  placement?: Placement;
  /** `dark` (default) for hints; `light` for a richer explanation. */
  variant?: 'dark' | 'light';
};

const HOVER_QUERY = '(hover: hover) and (min-width: 1024px)';

/**
 * A short explanation on focus (always) and on hover (from 1024px, pointer devices only). Stays open
 * while the pointer moves onto it, closes on Escape, and describes its trigger for screen readers.
 */
export function Tooltip({ content, children, placement = 'top-center', variant = 'dark' }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = `tt${useId().replace(/:/g, '')}`;
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { style, update } = usePosition(anchorRef, tipRef, { open, placement, offset: 8 });
  useDismiss({ open, onDismiss: () => setOpen(false), refs: [anchorRef, tipRef], outsidePress: false });
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const canHover = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(HOVER_QUERY).matches;
  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hideSoon = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const trigger = isValidElement(children)
    ? cloneElement(children, { 'aria-describedby': open ? id : undefined })
    : children;

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex"
        onFocus={show}
        onBlur={hideSoon}
        onPointerEnter={() => canHover() && show()}
        onPointerLeave={() => canHover() && hideSoon()}
      >
        {trigger}
      </span>
      {open ? (
        <Portal>
          <div
            ref={(el) => {
              tipRef.current = el;
              if (el) update();
            }}
            id={id}
            role="tooltip"
            style={style}
            onPointerEnter={show}
            onPointerLeave={hideSoon}
            className={cx(
              'z-(--jc-z-tooltip) max-w-70 rounded-jc-info px-3 py-2 text-sm shadow-jc-lg ring-1 ring-jc-card-border',
              variant === 'dark' ? 'bg-jc-readout-bg text-jc-readout-ink' : 'bg-jc-surface text-jc-ink',
            )}
          >
            {content}
          </div>
        </Portal>
      ) : null}
    </>
  );
}
